import React, { useCallback, useEffect, useState } from 'react'
import { Card, Table, Tooltip, Button, Select, Form, DatePicker, message, Input, Row, Col, Drawer } from 'antd';
import { CloudDownloadOutlined, SearchOutlined, EyeOutlined, FilterOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import apiClient from 'services/apiClient';
import { useQuery } from '@tanstack/react-query';
import { useMyContext } from 'Context/MyContextProvider';
import { useTemplates } from 'hooks/api';
import { ROW_GUTTER } from 'constants/ThemeConstant';
import NewMobilePreview from '../Campaign/newMobilePreview';
import { UserSelectField } from '../CustomUtils.js/CommonFieldUtils';
import { getStatusIcon } from 'components/shared-components/AvatarStatus';
import { GetParams } from '../Utils/utils';
import { iso2ToFlagEmoji } from 'utils/phoneCountry';
import CustomDrawer from 'components/shared-components/CustomDrawer';

const { RangePicker } = DatePicker;
const { Option } = Select;

const OutReportsFilters = ({
    isMobile,
    userId,
    onUserChange,
    dateRange,
    onDateRangeChange,
    category,
    onCategoryChange,
    country,
    onCountryChange,
    pricingCountries,
    userRole
}) => {
    if (isMobile) {
        return (
            <Form layout="vertical">
                {(userRole === 'Admin' || userRole === 'Reseller') && (
                    <Form.Item label="User" className="mb-4">
                        <UserSelectField value={userId} HandleUserChange={onUserChange} />
                    </Form.Item>
                )}
                <Form.Item label="Date Range" className="mb-4">
                    <RangePicker
                        placeholder={['Start Date', 'End Date']}
                        style={{ width: '100%' }}
                        value={dateRange}
                        allowClear
                        onChange={onDateRangeChange}
                    />
                </Form.Item>
                <Form.Item label="Category" className="mb-4">
                    <Select
                        allowClear
                        showSearch
                        placeholder="Category"
                        value={category || undefined}
                        style={{ width: '100%' }}
                        onChange={onCategoryChange}
                    >
                        <Option value="service">Service</Option>
                        <Option value="utility">Utility</Option>
                        <Option value="marketing">Marketing</Option>
                    </Select>
                </Form.Item>
                {pricingCountries.length > 0 && (
                    <Form.Item label="Country" className="mb-4">
                        <Select
                            allowClear
                            showSearch
                            optionFilterProp="label"
                            placeholder="Country"
                            style={{ width: '100%' }}
                            value={country || undefined}
                            onChange={onCountryChange}
                            options={pricingCountries.map((c) => ({
                                value: c.country_iso2,
                                label: `${iso2ToFlagEmoji(c.country_iso2)} ${c.country_name || c.country_iso2}`,
                            }))}
                        />
                    </Form.Item>
                )}
            </Form>
        );
    }

    return (
        <>
            {(userRole === 'Admin' || userRole === 'Reseller') && (
                <Col span={24} lg={12} xl={4}>
                    <div className="mb-3">
                        <Form.Item className='mb-0'>
                            <UserSelectField value={userId} HandleUserChange={onUserChange} />
                        </Form.Item>
                    </div>
                </Col>
            )}
            <Col span={24} lg={12} xl={6}>
                <div className="mb-3">
                    <Form.Item className='mb-0'>
                        <RangePicker
                            placeholder={['Start Date', 'End Date']}
                            style={{ width: '100%' }}
                            allowClear 
                            onChange={onDateRangeChange}
                        />
                    </Form.Item>
                </div>
            </Col>
            <Col span={24} lg={12} xl={3}>
                <div className="mb-3">
                    <Form.Item className='mb-0'>
                        <Select
                            allowClear
                            showSearch
                            placeholder="Category"
                            value={category || undefined}
                            style={{ width: '100%' }}
                            onChange={onCategoryChange}
                        >
                            <Option value="service">Service</Option>
                            <Option value="utility">Utility</Option>
                            <Option value="marketing">Marketing</Option>
                        </Select>
                    </Form.Item>
                </div>
            </Col>
            {pricingCountries.length > 0 && (
                <Col span={24} lg={12} xl={3}>
                    <div className="mb-3">
                        <Form.Item className='mb-0'>
                            <Select
                                allowClear
                                showSearch
                                optionFilterProp="label"
                                placeholder="Country"
                                style={{ width: '100%' }}
                                value={country || undefined}
                                onChange={onCountryChange}
                                options={pricingCountries.map((c) => ({
                                    value: c.country_iso2,
                                    label: `${iso2ToFlagEmoji(c.country_iso2)} ${c.country_name || c.country_iso2}`,
                                }))}
                            />
                        </Form.Item>
                    </div>
                </Col>
            )}
        </>
    );
};
const OutReports = () => {
    const { api, authToken, UserData, HandleExport, userRole, isMobile } = useMyContext();
    const { templates, refetch: refetchTemplates } = useTemplates();

    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [userId, setUserId] = useState(UserData?.id);
    const [searchValue, setSearchValue] = useState('');
    const [category, setCategory] = useState('');
    const [country, setCountry] = useState('');
    const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [draftUserId, setDraftUserId] = useState(userId);
    const [draftStartDate, setDraftStartDate] = useState(startDate);
    const [draftEndDate, setDraftEndDate] = useState(endDate);
    const [draftCategory, setDraftCategory] = useState(category);
    const [draftCountry, setDraftCountry] = useState(country);
    const [draftDateRange, setDraftDateRange] = useState(null);

    const isFilterApplied = userId !== UserData?.id || !!startDate || !!endDate || !!category || !!country;

    const handleDraftDateRangeChange = (dates) => {
        setDraftDateRange(dates);
        if (dates && dates.length > 0) {
            const sortedDates = dates.slice().sort((a, b) => new Date(a) - new Date(b));
            const formattedDates = sortedDates.map(date => dayjs(date).format('DD/MM/YY'));
            setDraftStartDate(formattedDates[0]);
            setDraftEndDate(formattedDates[formattedDates.length - 1]);
        } else {
            setDraftStartDate(null);
            setDraftEndDate(null);
        }
    };

    const openFilterDrawer = () => {
        setDraftUserId(userId);
        setDraftStartDate(startDate);
        setDraftEndDate(endDate);
        setDraftCategory(category);
        setDraftCountry(country);
        if (startDate && endDate) {
            setDraftDateRange([
                dayjs(startDate, 'DD/MM/YY'),
                dayjs(endDate, 'DD/MM/YY')
            ]);
        } else {
            setDraftDateRange(null);
        }
        setDrawerVisible(true);
    };

    const handleApplyFilters = () => {
        setUserId(draftUserId);
        setStartDate(draftStartDate);
        setEndDate(draftEndDate);
        setCategory(draftCategory);
        setCountry(draftCountry);
        resetToFirstPage();
        setDrawerVisible(false);
    };

    const handleClearFilters = () => {
        setUserId(UserData?.id);
        setStartDate(null);
        setEndDate(null);
        setCategory('');
        setCountry('');
        resetToFirstPage();
        setDrawerVisible(false);
    };

    const { current, pageSize } = pagination;

    const fetchOutReportsQuery = async () => {
        const params = {
            ...GetParams(startDate, endDate, searchValue),
            page: current,
            per_page: pageSize,
        };
        if (category) {
            params.category = category.toLowerCase();
        }
        if (country) {
            params.country = country;
        }
        const response = await apiClient.get(`/out-reports/${userId}`, { params });
        return response.data?.data ?? response.data;
    };

    const { data, isLoading: loading } = useQuery({
        queryKey: ['out-reports', userId, startDate, endDate, searchValue, category, country, current, pageSize],
        queryFn: fetchOutReportsQuery,
        enabled: !!userId,
        keepPreviousData: true,
    });

    const { data: pricingCountries = [] } = useQuery({
        queryKey: ['out-reports-countries', userId],
        queryFn: async () => {
            const response = await apiClient.get(`${api}user-pricing/${userId}/countries`, {
                headers: { Authorization: `Bearer ${authToken}` },
            });
            return response.data?.data ?? [];
        },
        enabled: !!userId,
        staleTime: 5 * 60 * 1000,
    });

    const reports = data?.out_reports || [];
    const total = data?.pagination?.total || 0;

    const resetToFirstPage = () => {
        setPagination((prev) => ({ ...prev, current: 1 }));
    };

    const HandleUserChange = (value) => {
        setUserId(value);
        resetToFirstPage();
    };

    const HandleDateRangeChange = (dates) => {
        if (dates && dates.length > 0) {
            const sortedDates = dates.slice().sort((a, b) => new Date(a) - new Date(b));
            const formattedDates = sortedDates.map(date => dayjs(date).format('DD/MM/YY'));
            setStartDate(formattedDates[0]);
            setEndDate(formattedDates[formattedDates.length - 1]);
        } else {
            setStartDate(null);
            setEndDate(null);
        }
        resetToFirstPage();
    };

    const handleInputChange = (e) => {
        setSearchValue(e.target.value.toLowerCase());
        resetToFirstPage();
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            resetToFirstPage();
        }
    };

    const handleCategoryChange = (value) => {
        setCategory(value);
        resetToFirstPage();
    };

    const handleCountryChange = (value) => {
        setCountry(value || '');
        resetToFirstPage();
    };

    const handleTableChange = (tablePagination) => {
        setPagination((prev) => ({
            ...prev,
            current: tablePagination.current,
            pageSize: tablePagination.pageSize,
        }));
    };

    const [errorData, setErrorData] = useState();

    useEffect(() => {
        ErrorCodes();
    }, []);

    const ErrorCodes = async () => {
        try {
            const response = await apiClient.get(`/error-codes`);
            const data = response.data.codes;
            setErrorData(data);
        } catch (error) {
        }
    };
    const PasreErrorMessage = (code) => {
        if (code) {
            const message = errorData?.find(item => item.code === parseInt(code));
            return message?.description;
        }
    };

    const tableColumns = [
        {
            title: 'Message Category',
            dataIndex: 'category',
            render: status => (
                <div className="text-capitalize" >
                    {status}
                </div>
            ),
            sorter: {
                compare: (a, b) => a.text_body.length - b.text_body.length,
            },
        },
        {
            title: 'To',
            dataIndex: 'recipient_id',
            sorter: {
                compare: (a, b) => a.recipient_id.length - b.recipient_id.length,
            },
        },
        {
            title: 'Sent Time',
            dataIndex: 'created_at',
            render: (created_at, record) => {
                const dateVal = created_at || record.timestamp;
                if (!dateVal) return <span>N/A</span>;
                const date = dayjs(dateVal);
                const formattedDate = date.format('MM-DD-YYYY');
                const formattedTime = date.format('hh:mm:ss A');
                return (
                    <span>
                        {formattedDate} | {formattedTime}
                    </span>
                );
            },
            sorter: {
                compare: (a, b) => (a.created_at || a.timestamp || '').localeCompare(b.created_at || b.timestamp || ''),
            },
        },
        {
            title: 'Delivered Time',
            dataIndex: 'delivered_time',
            render: (delivered_time) => {
                return delivered_time ? delivered_time : 'N/A'
            },
            sorter: {
                compare: (a, b) => a.status.length - b.status.length,
            },
        },
        {
            title: 'Read Time',
            dataIndex: 'read_time',
            render: (read_time) => {

                return read_time ? read_time : 'N/A';
            },
            sorter: {
                compare: (a, b) => a.status.length - b.status.length,
            },
        },
        {
            title: 'status',
            dataIndex: 'status',
            render: (_, record) => {
                const errorMessage = PasreErrorMessage(record?.error_code);
                const tagContent = (
                    // <Tag color={getStatusColor(record.status)} style={{ textTransform: 'capitalize', letterSpacing: '1px' }}>
                    //     {record.status}
                    // </Tag>
                    getStatusIcon(record.status, errorMessage)
                );
                return record.status === 'failed' ? (
                    <Tooltip title={errorMessage || 'Unknown error'}>{tagContent}</Tooltip>
                ) : (
                    tagContent
                );
            },
            sorter: {
                compare: (a, b) => a.status.length - b.status.length,
            },
        },
        {
            title: 'Action',
            dataIndex: 'actions',
            render: (_, record) => {
                return (
                    <div className="text-right d-flex">
                        <Tooltip title="Message PreView">
                            <Button
                                type="primary"
                                className="mr-2"
                                icon={<EyeOutlined />}
                                onClick={() => { showDrawer(record) }}
                                size="small"
                            />
                        </Tooltip>
                    </div>
                )
            }
        },



    ];

    const [open, setOpen] = useState(false);
    const [template, setTemplate] = useState([]);
    const [mediaID, setMediaID] = useState(null);
    const showDrawer = (item) => {
        if (item?.api_report) {
            let templateName = item?.api_report?.template_name;
            let media_id = item?.api_report?.media_id;
            setMediaID(media_id)
            let buttonValues = JSON.parse(item?.api_report?.button_values);
            let bodyValues = JSON.parse(item?.api_report?.body_values);
            let template = templates?.find(t => t.name === templateName);
            const { header, body, footer, buttons } = extractTemplateSections(template);
            if (bodyValues?.length > 0) {
                body.text = bodyValues?.reduce((text, value, index) => {
                    const placeholder = `{{${index + 1}}}`;
                    return text?.replace(placeholder, value);
                }, body?.text);
            }
            if (buttons) {
                buttons?.buttons?.forEach((btn, index) => {
                    if (btn?.url?.endsWith("\"")) {
                        btn.url = btn?.url?.slice(0, -1);
                    }
                    if (btn?.example && btn?.example.length > 0) {
                        const exampleUrl = btn.example[0];
                        const staticPart = btn.url;

                        if (exampleUrl?.startsWith(staticPart)) {
                            btn.url = staticPart + buttonValues[index];
                        }
                    }
                });
            }
            setTemplate({ header, body, footer, buttons });
            setOpen(true);
        }
    };

    const extractTemplateSections = (template) => {
        let header = null, body = null, footer = null, buttons = null;
        template?.components?.forEach(section => {
            switch (section.type) {
                case "HEADER":
                    header = section;
                    break;
                case "BODY":
                    body = section;
                    break;
                case "FOOTER":
                    footer = section;
                    break;
                case "BUTTONS":
                    buttons = section;
                    break;
                default:
                    // Handle any other section types if needed
                    break;
            }
        });
        return { header, body, footer, buttons };
    }
    const onClose = () => {
        setOpen(false);
        setTemplate(null); // Reset template
        setMediaID(null); // Reset 
        refetchTemplates()
    };
    return (
        <>
            <Card bodyStyle={{ padding: '0px' }}>
                <div className="container-fluid">
                    <Form
                        name="basicInformation"
                        layout={`${isMobile ? 'vertical' : 'horizontal'} `}
                    >
                        <Row gutter={ROW_GUTTER} justify="space-between" align="middle" className="py-4">
                            <Col xs={24} sm={24} lg={6} xl={4}>
                                <h2>Outgoing Reports</h2>
                            </Col>
                            <Col xs={24} sm={24} lg={18} xl={20}>
                                {isMobile ? (
                                    <Row gutter={[8, 8]} justify="end" align="middle">
                                        <Col xs={14}>
                                            <div className="mb-3">
                                                <Form.Item className='mb-0'>
                                                    <Input placeholder="Search" prefix={<SearchOutlined />}
                                                        value={searchValue}
                                                        onChange={handleInputChange}
                                                        onKeyDown={handleKeyDown}
                                                    />
                                                </Form.Item>
                                            </div>
                                        </Col>
                                        <Col xs={5}>
                                            <div className="mb-3">
                                                <Button 
                                                    block
                                                    type={isFilterApplied ? 'primary' : 'default'}
                                                    icon={<FilterOutlined />} 
                                                    onClick={openFilterDrawer}
                                                />
                                            </div>
                                        </Col>
                                        <Col xs={5}>
                                            <div className="mb-3">
                                                <Button 
                                                    block
                                                    icon={<CloudDownloadOutlined />} 
                                                    onClick={() => HandleExport('export-reports', 'incoming-report.xlsx')} 
                                                />
                                            </div>
                                        </Col>
                                    </Row>
                                ) : (
                                    <Row gutter={ROW_GUTTER} justify="end" align="middle">
                                        <OutReportsFilters
                                            isMobile={false}
                                            userId={userId}
                                            onUserChange={HandleUserChange}
                                            onDateRangeChange={HandleDateRangeChange}
                                            category={category}
                                            onCategoryChange={handleCategoryChange}
                                            country={country}
                                            onCountryChange={handleCountryChange}
                                            pricingCountries={pricingCountries}
                                            userRole={userRole}
                                        />
                                        <Col span={14} lg={12} xl={4}>
                                            <div className="mb-3">
                                                <Form.Item className='mb-0'>
                                                    <Input placeholder="Search" prefix={<SearchOutlined />}
                                                        value={searchValue}
                                                        onChange={handleInputChange}
                                                        onKeyDown={handleKeyDown}
                                                    />
                                                </Form.Item>
                                            </div>
                                        </Col>

                                        <Col span={10} lg={12} xl={4}>
                                            <div className="mb-3">
                                                <Button block icon={<CloudDownloadOutlined />} onClick={() => HandleExport('export-reports', 'incoming-report.xlsx')}>Export Report</Button>
                                            </div>
                                        </Col>
                                    </Row>
                                )}
                            </Col>
                        </Row>
                    </Form>
                </div>


                <div className="table-responsive">
                    <Table
                        loading={loading}
                        columns={tableColumns}
                        dataSource={reports}
                        rowKey="id"
                        pagination={{
                            current: current,
                            pageSize: pageSize,
                            total: total,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50'],
                            showTotal: (total) => `Total ${total} reports`,
                        }}
                        onChange={handleTableChange}
                        expandable={{
                            expandedRowRender: (record) => <p className='ml-5'>{record.status_id}</p>,
                        }}

                    />
                </div>
                {/* <UserView data={selectedUser} visible={userProfileVisible} close={() => closeUserProfile()} /> */}
            </Card>
            <div className="">
                <Drawer
                    title="Message Preview"
                    placement="right"
                    closable={false}
                    onClose={onClose}
                    open={open}
                    getContainer={false}
                    style={{
                        position: 'absolute',
                    }}
                >

                    <NewMobilePreview
                        mediaLink={mediaID}
                        requestType={'template'}
                        template={template}
                    />
                </Drawer>
            </div>
            
            <CustomDrawer
                title="Filter Reports"
                open={drawerVisible}
                onClose={() => setDrawerVisible(false)}
                footer={
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {isFilterApplied && (
                            <Button onClick={handleClearFilters} style={{ flex: 1 }}>
                                Clear
                            </Button>
                        )}
                        <Button type="primary" onClick={handleApplyFilters} style={{ flex: 1 }}>
                            Apply
                        </Button>
                    </div>
                }
            >
                <OutReportsFilters
                    isMobile={true}
                    userId={draftUserId}
                    onUserChange={setDraftUserId}
                    dateRange={draftDateRange}
                    onDateRangeChange={handleDraftDateRangeChange}
                    category={draftCategory}
                    onCategoryChange={setDraftCategory}
                    country={draftCountry}
                    onCountryChange={setDraftCountry}
                    pricingCountries={pricingCountries}
                    userRole={userRole}
                />
            </CustomDrawer>
        </>

    );
};

export default OutReports
